import React from 'react';
import {
  ThemeProvider,
  CssBaseline,
  Box,
  Typography,
  Paper,
  Alert,
  TextField,
  Button,
  Stack,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import theme from './theme';
import SideNav, { drawerWidth } from './SideNav';
import { PROOF_TYPES } from './constants';

export default function ImportsPage() {
  // API Server configuration - same pattern as SandboxPage.js
  const defaultApiServer = process.env.REACT_APP_API_SERVER || 'local-api.actual.video';
  
  const [apiId, setApiId] = React.useState('import-dev');
  
  // Photo URL state (default enabled)
  const [usePhotoUrl, setUsePhotoUrl] = React.useState(true);
  const [photoUrl, setPhotoUrl] = React.useState('');
  const [photoUrlError, setPhotoUrlError] = React.useState('');
  
  // Phone number state (default disabled)
  const [usePhone, setUsePhone] = React.useState(false);
  const [phoneInput, setPhoneInput] = React.useState('');
  const [phoneError, setPhoneError] = React.useState('');
  
  // Required proofs state
  const [requiredProofs, setRequiredProofs] = React.useState([]);
  const [customProof, setCustomProof] = React.useState('');
  
  const [jsonBlob, setJsonBlob] = React.useState('');
  const [jsonError, setJsonError] = React.useState('');
  const [submissionMessage, setSubmissionMessage] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [responseUrl, setResponseUrl] = React.useState('');

  const normalizePhone = (value) => value.replace(/[^0-9+]/g, '');

  const validatePhone = (value) => {
    const digitsOnly = value.replace(/\D/g, '');
    if (digitsOnly.length < 10) {
      return 'Enter at least 10 digits.';
    }
    if (digitsOnly.length > 15) {
      return 'Phone number is too long.';
    }
    return '';
  };

  const validateJsonBlob = (value) => {
    try {
      JSON.parse(value);
      return '';
    } catch (error) {
      return 'Invalid JSON format.';
    }
  };

  const validatePhotoUrl = (value) => {
    if (!value.trim()) {
      return 'Photo URL is required.';
    }
    try {
      new URL(value);
      return '';
    } catch (error) {
      return 'Please enter a valid URL.';
    }
  };

  const handlePhoneChange = (e) => {
    const value = normalizePhone(e.target.value);
    setPhoneInput(value);
    setPhoneError(validatePhone(value));
  };

  const handleJsonBlobChange = (e) => {
    const value = e.target.value;
    setJsonBlob(value);
    setJsonError(validateJsonBlob(value));
  };

  const handlePhotoUrlChange = (e) => {
    const value = e.target.value;
    setPhotoUrl(value);
    setPhotoUrlError(validatePhotoUrl(value));
  };

  const handleProofChange = (proof) => {
    setRequiredProofs(prev => {
      const newProofs = prev.includes(proof)
        ? prev.filter(p => p !== proof)
        : [...prev, proof];
      return newProofs;
    });
  };

  const handleAddCustomProof = () => {
    if (customProof.trim() && !requiredProofs.includes(customProof.trim())) {
      setRequiredProofs(prev => [...prev, customProof.trim()]);
      setCustomProof('');
    }
  };

  const handleRemoveCustomProof = (proof) => {
    // Check if this proof is not in the predefined PROOF_TYPES
    const isPredefinedProof = PROOF_TYPES.some(p => p.type === proof);
    if (!isPredefinedProof) {
      setRequiredProofs(prev => prev.filter(p => p !== proof));
    }
  };

  // Helper function to check if photo verification should be enabled
  const isPhotoVerificationEnabled = () => {
    const hasName = requiredProofs.includes('name');
    const hasAge = requiredProofs.includes('pg_age');
    const hasCustomProof = requiredProofs.some(proof => 
      !PROOF_TYPES.some(p => p.type === proof)
    );
    return hasName || hasAge || hasCustomProof;
  };

  // Clear photo verification when conditions are no longer met
  React.useEffect(() => {
    const hasName = requiredProofs.includes('name');
    const hasAge = requiredProofs.includes('pg_age');
    const hasCustomProof = requiredProofs.some(proof => 
      !PROOF_TYPES.some(p => p.type === proof)
    );
    const isEnabled = hasName || hasAge || hasCustomProof;
    
    if (!isEnabled && requiredProofs.includes('pg_photo_verification_url')) {
      setRequiredProofs(prev => prev.filter(p => p !== 'pg_photo_verification_url'));
    }
  }, [requiredProofs]);

  const canSubmit = (() => {
    // At least one matching parameter must be enabled
    const hasPhotoUrl = usePhotoUrl && !photoUrlError && photoUrl;
    const hasPhone = usePhone && !phoneError && phoneInput;
    const hasAtLeastOneMatchingParam = hasPhotoUrl || hasPhone;
    
    const hasValidJson = !jsonError && jsonBlob;
    return hasAtLeastOneMatchingParam && hasValidJson;
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    
    setIsSubmitting(true);
    setSubmissionMessage('');
    setResponseUrl('');
    
    try {
      // Build matching_params object only with enabled parameters
      const matchingParams = {};
      
      if (usePhotoUrl && photoUrl) {
        matchingParams.photo_url = photoUrl;
      }
      
      if (usePhone && phoneInput) {
        matchingParams.phone_number = phoneInput.replace(/\D/g, ''); // Remove non-digits
      }

      const requestBody = {
        matching_params: matchingParams,
        claims: JSON.parse(jsonBlob),
        requiredProofs: requiredProofs,
      };

      const response = await fetch(
        `https://${defaultApiServer}/v2/link/import/${apiId}`,
        {
          method: "PUT",
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status === 'ok') {
        setSubmissionMessage('Import link created successfully!');
        setResponseUrl(data.url);
      } else {
        throw new Error('Unexpected response format');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmissionMessage(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex' }}>
        {/* Navigation Sidebar */}
        <SideNav paperBg={'#fafafa'} titleColor={'#1976d2'} />

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 4,
            width: `calc(100% - ${drawerWidth}px)`
          }}
        >
          <Typography variant="h1" sx={{ mb: 3, fontWeight: 700 }}>
            Import Custom Identity Credentials
          </Typography>

          <Typography variant="h5" sx={{ mb: 3, color: '#666', lineHeight: 1.6 }}>
            Import verifiable credentials into Polyguard via a secure QR code link and prove you match the required parameters.
          </Typography>

          <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              How it works
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
              A credential issuer provides a QR code that encodes a link using the <code>securelink://</code> schema. 
              When opened in the Polyguard mobile app, the link prompts the user to import a verifiable credential. 
              Each credential specifies a set of <strong>match parameters</strong> that must be proven before import—most commonly a phone number and a selfie. 
              Once those parameters are verified, Polyguard decrypts and loads the rest of the credential attributes on-device.
            </Typography>
            <Alert severity="info">
              Use the form below to simulate capturing the match parameters. This helps you develop and test the mobile import flow.
            </Alert>
          </Paper>

          <Paper component="form" onSubmit={handleSubmit} elevation={1} sx={{ p: 3, mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              Create Importable Identity Link
            </Typography>

            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  API ID
                </Typography>
                <TextField
                  fullWidth
                  placeholder="e.g. import-dev"
                  value={apiId}
                  onChange={(e) => setApiId(e.target.value)}
                  helperText="The API ID to use in the import URL endpoint."
                />
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Matching Parameters
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Select at least one matching parameter to verify the user's identity.
                </Typography>
                
                <Stack direction="row" spacing={2}>
                  {/* Photo URL - comes first, default enabled */}
                  <Box sx={{ flex: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={usePhotoUrl}
                          onChange={(e) => setUsePhotoUrl(e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Photo URL
                        </Typography>
                      }
                    />
                    <TextField
                      fullWidth
                      placeholder="e.g. https://example.com/photo.jpg"
                      value={photoUrl}
                      onChange={handlePhotoUrlChange}
                      disabled={!usePhotoUrl}
                      error={Boolean(photoUrlError) && usePhotoUrl}
                      helperText={usePhotoUrl ? (photoUrlError || 'Enter a valid URL for the photo to be used for matching.') : 'Check the box above to enable this field.'}
                      sx={{ mt: 1 }}
                    />
                  </Box>

                  {/* Phone Number - comes second, default disabled */}
                  <Box sx={{ flex: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={usePhone}
                          onChange={(e) => setUsePhone(e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Phone Number
                        </Typography>
                      }
                    />
                    <TextField
                      fullWidth
                      placeholder="e.g. +14155552671"
                      value={phoneInput}
                      onChange={handlePhoneChange}
                      disabled={!usePhone}
                      inputMode="tel"
                      error={Boolean(phoneError) && usePhone}
                      helperText={usePhone ? (phoneError || 'Enter a valid mobile number (10–15 digits).') : 'Check the box above to enable this field.'}
                      sx={{ mt: 1 }}
                    />
                  </Box>
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  JSON Blob
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={jsonBlob}
                  onChange={handleJsonBlobChange}
                  error={Boolean(jsonError)}
                  helperText={jsonError || 'Enter JSON object that will be used as "claims" in the request (e.g., {"claim_name": "claim_value"}).'}
                  placeholder='{"claim_name": "claim_value"}'
                  sx={{ fontFamily: 'monospace' }}
                />
              </Box>

              <Box>
                <Accordion defaultExpanded={false}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="required-proofs-content"
                    id="required-proofs-header"
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Required Proofs (Optional)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                      Select the proofs that users must provide when importing this credential. If none are selected, an empty list will be sent.
                    </Typography>
                    
                    <Grid container spacing={2}>
                      {/* Split PROOF_TYPES into two columns */}
                      <Grid item xs={6}>
                        <FormGroup>
                          {PROOF_TYPES.slice(0, Math.ceil(PROOF_TYPES.length / 2)).map((proof) => (
                            <FormControlLabel
                              key={proof.type}
                              control={
                                <Checkbox
                                  checked={requiredProofs.includes(proof.type)}
                                  onChange={() => handleProofChange(proof.type)}
                                  disabled={
                                    proof.disabled || 
                                    (proof.type === 'pg_photo_verification_url' && !isPhotoVerificationEnabled())
                                  }
                                />
                              }
                              label={proof.label}
                            />
                          ))}
                        </FormGroup>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <FormGroup>
                          {PROOF_TYPES.slice(Math.ceil(PROOF_TYPES.length / 2)).map((proof) => (
                            <FormControlLabel
                              key={proof.type}
                              control={
                                <Checkbox
                                  checked={requiredProofs.includes(proof.type)}
                                  onChange={() => handleProofChange(proof.type)}
                                  disabled={
                                    proof.disabled || 
                                    (proof.type === 'pg_photo_verification_url' && !isPhotoVerificationEnabled())
                                  }
                                />
                              }
                              label={proof.label}
                            />
                          ))}
                        </FormGroup>
                      </Grid>
                    </Grid>

                    {/* Custom Proof Input */}
                    <Box sx={{ mt: 3 }}>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, fontWeight: 500 }}
                      >
                        Custom Proof:
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "center",
                        }}
                      >
                        <TextField
                          size="small"
                          placeholder="e.g. custom_claim"
                          value={customProof}
                          onChange={(e) => setCustomProof(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleAddCustomProof();
                            }
                          }}
                          sx={{ flex: 1 }}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleAddCustomProof}
                          disabled={!customProof.trim()}
                          sx={{ height: "40px" }}
                        >
                          Add
                        </Button>
                      </Box>

                      {/* Display custom proofs as chips */}
                      {requiredProofs.filter(
                        (proof) =>
                          !PROOF_TYPES.some((p) => p.type === proof)
                      ).length > 0 && (
                        <Box
                          sx={{
                            mt: 1,
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                          }}
                        >
                          {requiredProofs
                            .filter(
                              (proof) =>
                                !PROOF_TYPES.some((p) => p.type === proof)
                            )
                            .map((proof) => (
                              <Chip
                                key={proof}
                                label={proof}
                                size="small"
                                onDelete={() =>
                                  handleRemoveCustomProof(proof)
                                }
                              />
                            ))}
                        </Box>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>

              <Box>
                <Button type="submit" variant="contained" disabled={!canSubmit || isSubmitting}>
                  {isSubmitting ? 'Creating Import Link...' : 'Create Import Link'}
                </Button>
                {submissionMessage && (
                  <Alert severity={responseUrl ? "success" : "error"} sx={{ mt: 2 }}>
                    {submissionMessage}
                  </Alert>
                )}
                {responseUrl && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                      Import Link Created
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => window.open(responseUrl, '_blank')}
                      sx={{ mr: 2 }}
                    >
                      Open QR Code Page
                    </Button>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      URL: {responseUrl}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Box>
    </ThemeProvider>
  );
}